import { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { message } from "antd";
import { Creators as AuthCreators } from "../../services/redux/auth/actions";
import { Creators as CartCreators } from "../../services/redux/cart/actions";
import { checkAuth } from "../../util/helpers/requireAuthentication";
import { history } from "../../util/helpers/browserHistory";
import { isInRouteDeskFrame } from "../../util/helpers/deskPanelLook";
import { announceLoginReady, phoneFromMessage, isLoginManualMessage } from "../../util/helpers/deskLoginPhone";
import { deskGuardDecision, deskAccountStatusFrom, setDeskAccountStatus } from "../../util/helpers/deskLockdown";
import { signOutCleanup } from "../../util/helpers/signOutCleanup";

// Desk build only, inside Route Desk's panel. Renders nothing.
//
// Route Desk answers every "login-ready" from its panel with the officer's
// own employee phone (or, for a superadmin, "manual"). This guard asks
// whenever it is signed in without an answer yet (throttled), and also hears
// the answers to the Sign In page's own ask. When the signed-in account is not that officer's employee
// account, it signs out (the same clean-up as Log out, but without keeping
// the old phone) and opens Sign In, which then locks to the right phone.
// "Manual" means no check. Outside a frame it does nothing at all.
// Its verdict (deskLockdown.js setDeskAccountStatus) tells DeskOrderingFor
// when the ?ordering_for= CHE may be applied: only to a confirmed account.
const ASK_EVERY_MS = 5000;

const DeskAccountGuard = ({ user, isFetching, logout, resetOrder }) => {
  const [routeDeskPhone, setRouteDeskPhone] = useState(null);
  const [manual, setManual] = useState(false);
  const signingOut = useRef(false);
  const lastAsked = useRef(0);

  useEffect(() => {
    if (!isInRouteDeskFrame()) return undefined;
    const onMessage = (event) => {
      const phone = phoneFromMessage(event);
      if (phone) {
        setRouteDeskPhone(phone);
        setManual(false);
      } else if (isLoginManualMessage(event)) {
        setRouteDeskPhone(null);
        setManual(true);
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, []);

  useEffect(() => {
    const signedIn = checkAuth();
    // One sign-out per sign-in: ready again once signed out.
    if (!signedIn) signingOut.current = false;
    // Signed in (at start, or later without the Sign In page, e.g. through
    // the refresh token) and no answer from Route Desk yet: ask, at most
    // once every ASK_EVERY_MS.
    if (isInRouteDeskFrame() && signedIn && !routeDeskPhone && !manual &&
        Date.now() - lastAsked.current > ASK_EVERY_MS) {
      lastAsked.current = Date.now();
      announceLoginReady();
    }
    const decision = deskGuardDecision({
      inFrame: isInRouteDeskFrame(),
      signedIn,
      isFetching,
      user,
      routeDeskPhone,
    });
    setDeskAccountStatus(deskAccountStatusFrom({ manual: manual && signedIn, decision }));
    if (decision !== "sign-out" || signingOut.current) return;
    signingOut.current = true;
    console.log("desk account guard: wrong account, signing out");
    signOutCleanup({ logout, resetOrder, keepPhone: false });
    setDeskAccountStatus("unknown");
    message.warning("This was not your employee account. Sign in with your own account.", 8);
    history.push("/login");
  }, [user, isFetching, routeDeskPhone, manual, logout, resetOrder]);

  return null;
};

const mapStateToProps = (state) => ({
  user: state?.auth?.user,
  isFetching: state?.auth?.isFetching,
});

const mapDispatchToProps = (dispatch) => ({
  logout: () => dispatch(AuthCreators.logout()),
  resetOrder: () => dispatch(CartCreators.resetOrder()),
});

export default connect(mapStateToProps, mapDispatchToProps)(DeskAccountGuard);
